import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Config } from 'src/app/config/config';
import { Document, Metadata, DocumentResolved } from 'src/app/beans/health-record/other-documents.bean';
import * as moment from 'moment';
import { JwtHelper } from 'src/app/services/core/jwt-helper.service';
import { MatDialog } from '@angular/material/dialog';
import { S3Service } from 'src/app/services/health-record/other-documents/s3.service';
import { ListDocumentService } from 'src/app/services/health-record/other-documents/list-document.service';
import { UploadDocumentModalComponent } from './modals/upload-document-modal/upload-document-modal.component';
import { ModalService } from 'src/app/services/core/modal.service';
import { EheAnalyticsDirective } from '../../shared/ehe-analytics/ehe-analytics.directive';
import { AnalyticsBean } from 'src/app/beans/analytics/analytics-module.bean';

@Component({
	selector: 'app-other-documents',
	templateUrl: './other-documents.component.html',
	styleUrls: ['./other-documents.component.scss'],
	providers: [EheAnalyticsDirective],
})
export class OtherDocumentsComponent implements OnInit {
	documents: Document[];
	noDataMsg = 'No documents to display';
	noData = false;
	numberUploaded: number;
	storageLimit: number;
	totalBytes: number;
	remainingBytes: number;

	sidebarMenu: Array<any> = [
		{
			menuName: 'Additional Health Documents',
			routerLink: '/wellness/health/record/additional',
		},
		{
			menuName: 'EHE Reports',
			routerLink: '/wellness/health/record/report',
		},
	];
	analyticsData: AnalyticsBean;
	constructor(
		private s3Service: S3Service,
		public listService: ListDocumentService,
		private modalService: ModalService,
		private dialog: MatDialog,
		private jwtService: JwtHelper,
		private config: Config,
		private eheAnalyticsDirective: EheAnalyticsDirective,
		private route: ActivatedRoute
	) {
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'Other Documents';
		this.analyticsData.pageUrl = '/wellness/health/record';
		this.route.data.subscribe(data => {
			const resolvedData: DocumentResolved = data['resolvedData'];
			this.listService.setDocuments(resolvedData);
		});
		this.storageLimit = this.config.otherDocuments.UPLOAD_LIMIT;
	}

	ngOnInit(): void {
		this.listService.getDocuments().subscribe(docs => {
			if (docs) {
				this.documents = docs;
				const size = docs.map(data => +data.metadata.fileSize);
				this.totalBytes = this.getArraySum(size);
				this.remainingBytes = this.storageLimit - this.totalBytes;
				this.numberUploaded = docs.length;
			}
		});
	}

	maxSortOrder(): number {
		return Math.max.call(null, ...this.documents.map(c => c.metadata.sortOrder), 1);
	}

	openUploadDocumentModal() {
		const upConfig = this.config.content.fileUpload;
		const dialogRef = this.dialog.open(UploadDocumentModalComponent, {});
		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				const metadata = new Metadata();
				metadata.filename = result.filename;
				// Future functionality
				// metadata.category = result.category;
				metadata.description = result.description;
				metadata.epmsPID = this.jwtService.getEpmsId();
				metadata.uploadedDate = moment(new Date()).format('MM/DD/YYYY');
				metadata.lastModifiedDate = moment(new Date()).format('MM/DD/YYYY');
				metadata.fileSize = result.file.size ? result.file.size.toString() : '';
				metadata.extension = result.file.type;
				metadata.sortOrder = this.maxSortOrder() + 1;
				// check the uploaded file for eligibility

				if (!/(?:pdf|png|jpe?g|gif)$/i.test(metadata.extension)) {
					this.noData = true;
					this.modalService.openInfoModal('Error', 'info', upConfig.MSGFILEUP001, 'Okay');
					this.analyticsData.id = this.config.content.fileUpload.MSGFILEUP001;
					this.analyticsData.redirectedTo = '/wellness/health/record';
					this.analyticsData.placement = 'Upload document modal';
					this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
				} else if (+metadata.fileSize > this.storageLimit) {
					// error due to being too large
					this.noData = true;
					this.modalService.openInfoModal('Error', 'info', upConfig.MSGFILEUP002, 'Okay');
					this.analyticsData.id = this.config.content.fileUpload.MSGFILEUP002;
					this.analyticsData.redirectedTo = '/wellness/health/record';
					this.analyticsData.placement = 'Upload document modal';
					this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
				} else if (+metadata.fileSize > this.remainingBytes) {
					// error due to limited file size remaining
					this.noData = true;
					this.modalService.openInfoModal('Error', 'info', upConfig.MSGFILEUP003, 'Okay');
					this.analyticsData.id = this.config.content.fileUpload.MSGFILEUP003;
					this.analyticsData.redirectedTo = '/wellness/health/record';
					this.analyticsData.placement = 'Upload document modal';
					this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
				} else {
					// To upload file to s3
					this.noData = false;
					this.noDataMsg = null;
					this.s3Service.uploadObject(result.file, metadata);
				}
			}
		});
	}

	/** @function getArraySum -
	 * Method to return the sum of numbers within an array
	 * @param {number[]} array - The array of numbers in which to be calculated.
	 * @example
	 * <caption>Example usage of getArraySum().</caption>
	 *
	 * const numbers = [1, 2, 3, 4, 5];
	 * getArraySum(numbers);
	 * // Returns - 15
	 */

	getArraySum(array: number[]) {
		/**
		 * Instead of mutating any values by using a for loop and declaring var
		 * We can create a const and define a function 'add'
		 * We use the reduce method but only 2/4 arguments are needed (accumulator, currentValue)
		 * This iterates through until the end of the array is reached
		 */
		const add = (a, b) => a + b;
		const sum = array.reduce(add);
		return sum;
	}
}
